import queryString from 'query-string'
import React from 'react'
import { useHistory, useLocation } from 'react-router'

import authentication from 'api/authentication'
import CompanyListForm from 'components/authentication/CompanyListForm'
import { MFAForm } from 'components/authentication/MFAForm'
import SignInForm from 'components/authentication/SignInForm'
import Modal, { ModalHeader, ModalCloseButton } from 'components/Modal'
import MFATokenEntry from 'components/profile/MFATokenEntry'
import useAppRefresh from 'hooks/use-app-refresh'
import useAuth from 'hooks/use-auth'
import { MFAResponse, UserAuth, UserAuthCompany } from 'types/api/user/session'
import { messageFromResponseError } from 'utils/strings'

enum ACTIONS {
  RESET = 'reset',
  ENTER_TOKEN = 'set_token',
  SELECT_COMPANY = 'set_company',
  VALIDATE_CREDENTIALS = 'validate_credentials',
  UPDATE_EMAIL = 'update_email',
  UPDATE_PASSWORD = 'update_password',
  UPDATE_TOKEN = 'update_token',
  UPDATE_COMPANY_NAME = 'update_company_name'
}

export enum Mode {
  Credentials = 'credentials',
  Token = 'token',
  Companies = 'companies'
}

export interface SignInCredentials {
  mode: Mode
  email: string
  password: string
  token: string
  companyName: string
  companyList: UserAuthCompany[]
  mfaResponse: UserAuth
}

type Action =
  | { type: ACTIONS.RESET }
  | { type: ACTIONS.ENTER_TOKEN; mfaResponse?: MFAResponse }
  | { type: ACTIONS.SELECT_COMPANY; companyList: UserAuthCompany[] }
  | { type: ACTIONS.VALIDATE_CREDENTIALS; mfaResponse: UserAuth }
  | { type: ACTIONS.UPDATE_EMAIL; email: string }
  | { type: ACTIONS.UPDATE_PASSWORD; password: string }
  | { type: ACTIONS.UPDATE_TOKEN; token: string }
  | { type: ACTIONS.UPDATE_COMPANY_NAME; companyName: string }

const initialState: SignInCredentials = {
  mode: Mode.Credentials,
  email: '',
  password: '',
  token: '',
  companyName: '',
  companyList: [],
  mfaResponse: null
}

const signInReducer = (
  state: SignInCredentials,
  action: Action
): SignInCredentials => {
  switch (action.type) {
    case ACTIONS.RESET:
      return {
        ...initialState
      }

    case ACTIONS.ENTER_TOKEN:
      return {
        ...state,
        mode: Mode.Token,
        mfaResponse: null
      }

    case ACTIONS.SELECT_COMPANY:
      return {
        ...state,
        mode: Mode.Companies,
        companyList: action.companyList,
        mfaResponse: null
      }

    case ACTIONS.VALIDATE_CREDENTIALS:
      return {
        ...state,
        mode: Mode.Credentials,
        mfaResponse: action.mfaResponse
      }

    case ACTIONS.UPDATE_EMAIL:
      return {
        ...state,
        email: action.email
      }

    case ACTIONS.UPDATE_PASSWORD:
      return {
        ...state,
        password: action.password
      }

    case ACTIONS.UPDATE_TOKEN:
      return {
        ...state,
        token: action.token
      }

    case ACTIONS.UPDATE_COMPANY_NAME:
      return {
        ...state,
        companyName: action.companyName
      }

    default:
      return state
  }
}

const SignIn: React.FC<{ initialCredentials?: SignInCredentials }> = ({
  initialCredentials = initialState
}) => {
  const history = useHistory()
  const location = useLocation()
  const authState = useAuth()
  const appRefresh = useAppRefresh()

  const [signInCredentials, signInCredentialsDispatcher] = React.useReducer(
    signInReducer,
    initialCredentials
  )
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [signInError, setSignInError] = React.useState(null)

  const handlePasswordLogin = async (
    selectedCompany = signInCredentials.companyName
  ) => {
    try {
      signInCredentialsDispatcher({
        type: ACTIONS.UPDATE_COMPANY_NAME,
        companyName: selectedCompany
      })
      setSignInError(null)
      setIsSubmitting(true)

      const credentials: UserAuth | 'useMfaAuth' = await authentication.signin({
        email: signInCredentials.email,
        password: signInCredentials.password,
        token: signInCredentials.token,
        companyName: selectedCompany
      })

      if (credentials === 'useMfaAuth') {
        signInCredentialsDispatcher({ type: ACTIONS.ENTER_TOKEN })
        setIsSubmitting(false)
        return
      } else if (credentials && credentials.result === 'chooseCompany') {
        signInCredentialsDispatcher({
          type: ACTIONS.SELECT_COMPANY,
          companyList: credentials.companies
        })
        setIsSubmitting(false)
        return
      } else if (credentials?.qrUrl && credentials.secretKey) {
        // Need to validate your mfaToken
        signInCredentialsDispatcher({
          type: ACTIONS.VALIDATE_CREDENTIALS,
          mfaResponse: credentials
        })
        setIsSubmitting(false)
        return
      }

      await authState.startSession(credentials)

      // Refresh Webapp When Logging In
      // since we're setting the href explicitly below, that reloads the webapp
      // so if it's marked as needing to be refreshed, then we can clear that here
      appRefresh.markAsRefreshed()

      const { next } = queryString.parse(location.search)
      if (next) {
        window.location.href = Array.isArray(next) ? next[0] : next
      } else {
        window.location.href = '/'
      }
    } catch (err) {
      handleError(err, 'Invalid username or password.')
      setIsSubmitting(false)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      const { next } = queryString.parse(location.search)

      // same as above with password login
      // we're refreshing the webapp since we reload the page here
      appRefresh.markAsRefreshed()

      window.location.href = await authentication.generateGoogleUrl({
        nextPathname: next
      })
    } catch (err) {
      handleError(
        err,
        'There was an issue communicating with Google. Please try again later.'
      )
    }
  }

  const handleSamlLogin = async () => {
    history.push('/login/saml')
  }

  const handleError = (err, defaultText) => {
    const errorText = messageFromResponseError(err, defaultText)
    setSignInError(errorText)
  }

  if (signInCredentials.mode === Mode.Token) {
    return (
      <MFAForm
        handleBack={() => signInCredentialsDispatcher({ type: ACTIONS.RESET })}
        handleTokenChange={(token) =>
          signInCredentialsDispatcher({ type: ACTIONS.UPDATE_TOKEN, token })
        }
        handleTokenSubmission={handlePasswordLogin}
        signInError={signInError}
        submitting={isSubmitting}
        token={signInCredentials.token}
      />
    )
  }

  if (signInCredentials.mode === Mode.Companies) {
    return (
      <CompanyListForm
        companies={signInCredentials.companyList}
        handleBack={() => signInCredentialsDispatcher({ type: ACTIONS.RESET })}
        handleCompanySelection={(companyName) =>
          handlePasswordLogin(companyName)
        }
        submitting={isSubmitting}
      />
    )
  }

  if (signInCredentials.mfaResponse) {
    return (
      <Modal isOpen={Boolean(signInCredentials.mfaResponse)}>
        <ModalHeader>
          <ModalCloseButton onClick={() => window.location.reload()} />
        </ModalHeader>
        <MFATokenEntry
          isSignIn
          email={signInCredentials.email}
          mfaResponse={signInCredentials.mfaResponse}
          validateMFASuccess={handlePasswordLogin}
          onCancel={() => window.location.reload()}
        />
      </Modal>
    )
  }

  return (
    <div>
      <SignInForm
        email={signInCredentials.email}
        handleEmailChange={(email) =>
          signInCredentialsDispatcher({ type: ACTIONS.UPDATE_EMAIL, email })
        }
        handleGoogleLogin={handleGoogleLogin}
        handlePasswordChange={(password) =>
          signInCredentialsDispatcher({
            type: ACTIONS.UPDATE_PASSWORD,
            password
          })
        }
        handlePasswordLogin={handlePasswordLogin}
        handleSamlLogin={handleSamlLogin}
        password={signInCredentials.password}
        signInError={signInError}
        submitting={isSubmitting}
      />
    </div>
  )
}

export default SignIn
